/**
 * Before-After component for portfolio showcases
 * Side-by-side image comparison with elegant divider
 */

import Image from 'next/image';
import { cn } from '@/lib/utils';

interface BeforeAfterProps {
  beforeImage: string;
  afterImage: string;
  title: string;
  className?: string;
  size?: 'normal' | 'large';
}

export function BeforeAfter({
  beforeImage,
  afterImage,
  title,
  className,
  size = 'normal'
}: BeforeAfterProps) {
  const isLarge = size === 'large';
  const aspectRatio = isLarge ? 'aspect-video' : 'aspect-[4/3]';
  const titleSize = isLarge ? 'text-2xl' : 'text-lg';

  return (
    <div className={cn('relative group', className)}>
      <div className="grid md:grid-cols-2 gap-0.5 bg-primary/20 rounded-lg overflow-hidden before-after-divider shadow-2xl">
        {/* Before Image */}
        <div className={cn('relative bg-muted overflow-hidden', aspectRatio)}>
          <Image
            src={beforeImage}
            alt="Before transformation"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute top-4 left-4 z-10">
            <span className="px-4 py-2 bg-background/90 backdrop-blur text-xs font-semibold tracking-wider uppercase rounded-full shadow-lg">
              Before
            </span>
          </div>
        </div>

        {/* After Image */}
        <div className={cn('relative bg-muted overflow-hidden', aspectRatio)}>
          <Image
            src={afterImage}
            alt="After transformation"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute top-4 right-4 z-10">
            <span className="px-4 py-2 bg-primary text-primary-foreground backdrop-blur text-xs font-semibold tracking-wider uppercase rounded-full shadow-lg">
              After
            </span>
          </div>
        </div>
      </div>

      {/* Project Title */}
      <div className="mt-6">
        <h3 className={cn('font-semibold text-center group-hover:text-primary transition-colors', titleSize)}>
          {title}
        </h3>
      </div>
    </div>
  );
}
